import { useState, useEffect } from 'react';
import { companyGetSubscriptionStatus, CompanySubscriptionStatus } from '../api/company';
import { useAuth } from './use-auth';

export interface SubscriptionStatus extends CompanySubscriptionStatus { }

// Free accounts may create a single project; premium lifts the limit.
const FREE_PROJECT_LIMIT = 1;
const PREMIUM_PROJECT_LIMIT = 25;

export const useSubscriptionStatus = () => {
    const { projects } = useAuth();
    const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchSubscriptionStatus = async () => {
        try {
            console.log('=== SUBSCRIPTION STATUS DEBUG START ===');
            setLoading(true);
            setError(null);

            console.log('Hook: Fetching subscription status...');
            const result = await companyGetSubscriptionStatus();
            console.log('Hook: Subscription status API result:', result);

            if (result.success) {
                console.log('Hook: Setting subscription status:', result.data);
                setSubscriptionStatus(result.data);

                // Log key subscription details
                console.log('Hook: Subscription status:', result.data?.subscription_status);
                console.log('Hook: Has premium:', result.data?.subscription_status === 'premium');
                console.log('Hook: Company data:', result.data?.company);
                console.log('Hook: Stripe subscription ID:', result.data?.company?.stripe_subscription_id);
            } else {
                console.error('Hook: Failed to fetch subscription status');
                setError('Failed to fetch subscription status');
            }
        } catch (err) {
            console.error('Hook: Error fetching subscription status:', err);
            setError('Failed to fetch subscription status');
        } finally {
            setLoading(false);
            console.log('=== SUBSCRIPTION STATUS DEBUG END ===');
        }
    };

    const refreshSubscriptionStatus = () => {
        fetchSubscriptionStatus();
    };

    useEffect(() => {
        fetchSubscriptionStatus();
    }, []);

    const hasPremium = subscriptionStatus?.subscription_status === 'premium';
    // Count the user's own projects, not the Stripe seat quantity and not the
    // demo project auto-seeded at sign-up (is_example), which is free.
    const projectCount = (projects || []).filter((p) => !p?.is_example).length;
    const projectLimit = hasPremium ? PREMIUM_PROJECT_LIMIT : FREE_PROJECT_LIMIT;
    // Free users get their first project; the limit kicks in once they have one.
    const isAtProjectLimit = projectCount >= projectLimit;

    return {
        subscriptionStatus,
        loading,
        error,
        refreshSubscriptionStatus,
        // Helper functions
        canCreateProject: !isAtProjectLimit,
        hasPremium,
        projectCount,
        projectLimit,
        isAtProjectLimit
    };
};
